import { Head, Link, usePage } from '@inertiajs/react'
import MainLayout from '../../components/layout/MainLayout'

export default function PolitiqueConfidentialite() {
  const { lastUpdate } = usePage().props

  return (
    <MainLayout>
      <Head title="Politique de confidentialité — SmartImmo" />

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <header className="mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Politique de confidentialité</h1>
          <p className="text-sm text-gray-500">Dernière mise à jour : {lastUpdate}</p>
        </header>

        <div className="bg-blue-50 border-l-4 border-blue-500 text-blue-900 p-4 mb-10 rounded">
          <p className="text-sm leading-relaxed">
            PLUGANDWORK (Sinoia), éditeur de SmartImmo, accorde une importance fondamentale à la
            protection des données personnelles. La présente politique décrit comment vos données
            sont collectées, utilisées et protégées dans le cadre de votre utilisation du site
            <strong> smartimmo.sinoia.fr</strong> et du service SmartImmo, conformément au
            Règlement Général sur la Protection des Données (RGPD — UE 2016/679) et à la loi
            « Informatique et Libertés ».
          </p>
        </div>

        <nav aria-label="Sommaire" className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-12">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Sommaire</h2>
          <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">
            <li><a href="#responsable" className="hover:text-primary-600">Responsable du traitement</a></li>
            <li><a href="#donnees-collectees" className="hover:text-primary-600">Données collectées</a></li>
            <li><a href="#finalites" className="hover:text-primary-600">Finalités et bases légales</a></li>
            <li><a href="#destinataires" className="hover:text-primary-600">Destinataires et sous-traitants</a></li>
            <li><a href="#duree" className="hover:text-primary-600">Durée de conservation</a></li>
            <li><a href="#securite" className="hover:text-primary-600">Sécurité des données</a></li>
            <li><a href="#droits" className="hover:text-primary-600">Vos droits</a></li>
            <li><a href="#transferts" className="hover:text-primary-600">Transferts hors UE</a></li>
            <li><a href="#contact" className="hover:text-primary-600">Contact et réclamation</a></li>
          </ol>
        </nav>

        <article className="prose prose-gray max-w-none">
          <section id="responsable" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Responsable du traitement</h2>
            <p className="text-gray-700 mb-3">
              Le responsable du traitement des données personnelles est :
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded p-4 my-4 text-sm text-gray-800 leading-relaxed">
              <p><strong>PLUGANDWORK</strong> (Sinoia)</p>
              <p>SASU au capital de 30 864 € — SIREN 439 603 689 — RCS Nîmes</p>
              <p>132 vieille route d'Anduze, 30100 Alès, France</p>
              <p>Contact : <a href="mailto:contact@sinoia.fr" className="text-primary-600 hover:underline">contact@sinoia.fr</a></p>
            </div>
          </section>

          <section id="donnees-collectees" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Données collectées</h2>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.1 Prospects (visiteurs scannant un QR code)</h3>
            <p className="text-gray-700 mb-3">
              Lorsque vous scannez un QR code d'une agence partenaire et échangez avec notre
              agent conversationnel IA, nous pouvons collecter :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
              <li>Identité : nom, prénom</li>
              <li>Coordonnées : adresse e-mail, numéro de téléphone</li>
              <li>Projet immobilier : type de projet (achat, vente, location, mise en location), budget ou prix de vente, localisation souhaitée, surface, nombre de pièces, calendrier</li>
              <li>Historique de la conversation avec l'IA</li>
              <li>Données techniques liées au scan : adresse IP, type d'appareil (mobile, tablette, ordinateur), navigateur, date et heure, données de géolocalisation approximative dérivées de l'IP</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.2 Utilisateurs professionnels (agents immobiliers, agences)</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
              <li>Identifiants de connexion : adresse e-mail, mot de passe (chiffré)</li>
              <li>Données professionnelles : nom de l'agence, adresse, téléphone</li>
              <li>Données d'usage : connexions, actions effectuées dans l'interface, préférences</li>
              <li>Données de facturation : historique des achats de crédits, transactions</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.3 Visiteurs du site public</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Données de navigation collectées via cookies strictement nécessaires (voir <Link href="/politique-cookies" className="text-primary-600 hover:underline">politique cookies</Link>)</li>
              <li>Messages envoyés via le formulaire de contact</li>
            </ul>
          </section>

          <section id="finalites" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Finalités et bases légales</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left border border-gray-200 rounded">
                <thead className="bg-gray-50 text-gray-900">
                  <tr>
                    <th className="px-4 py-3 border-b border-gray-200">Finalité</th>
                    <th className="px-4 py-3 border-b border-gray-200">Base légale</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr>
                    <td className="px-4 py-3 border-b border-gray-200">Qualifier votre projet immobilier via l'IA et transmettre votre demande à l'agence concernée</td>
                    <td className="px-4 py-3 border-b border-gray-200">Intérêt légitime du prospect à être mis en relation avec un professionnel, exécution de mesures précontractuelles</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-gray-200">Fournir et exécuter le service SmartImmo aux agences partenaires</td>
                    <td className="px-4 py-3 border-b border-gray-200">Exécution du contrat</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-gray-200">Sécurité du service, prévention de la fraude et lutte contre les abus</td>
                    <td className="px-4 py-3 border-b border-gray-200">Intérêt légitime de PLUGANDWORK</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-gray-200">Statistiques internes d'usage et amélioration du service</td>
                    <td className="px-4 py-3 border-b border-gray-200">Intérêt légitime de PLUGANDWORK</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b border-gray-200">Facturation et tenue de la comptabilité</td>
                    <td className="px-4 py-3 border-b border-gray-200">Obligation légale</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Communications marketing (le cas échéant)</td>
                    <td className="px-4 py-3">Consentement préalable</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id="destinataires" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Destinataires et sous-traitants</h2>
            <p className="text-gray-700 mb-3">Vos données peuvent être communiquées :</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
              <li>
                <strong>À l'agence immobilière partenaire</strong> émettrice du QR code que vous
                avez scanné, dans le but exclusif de traiter votre demande
              </li>
              <li>
                <strong>Aux agences partenaires de cette agence</strong>, si la fonctionnalité de
                collaboration inter-agences a été activée, et uniquement pour les projets
                acheteur ou locataire compatibles avec un bien proposé par ces partenaires
              </li>
              <li>
                <strong>Aux équipes internes de PLUGANDWORK</strong> habilitées (support,
                administration de la plateforme)
              </li>
              <li>
                <strong>Aux autorités compétentes</strong>, en cas d'obligation légale ou de
                réquisition judiciaire
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Sous-traitants techniques</h3>
            <p className="text-gray-700 mb-3">
              Nous faisons appel aux sous-traitants suivants, qui interviennent strictement dans
              le cadre de nos instructions et conformément au RGPD :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li><strong>OVH SAS</strong> (France) — hébergement des serveurs applicatifs et de la base de données</li>
              <li><strong>OpenAI, L.L.C.</strong> (États-Unis) — modèle de langage utilisé par l'agent conversationnel IA pour qualifier les prospects, encadré par les clauses contractuelles types de la Commission européenne</li>
            </ul>
            <p className="text-gray-700 mt-3">
              Aucune donnée personnelle n'est vendue ni louée à des tiers à des fins commerciales.
            </p>
          </section>

          <section id="duree" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Durée de conservation</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Données de prospects qualifiés (leads)</strong> : 3 ans à compter du dernier contact avec l'agence destinataire, à des fins de prospection commerciale</li>
              <li><strong>Conversations IA</strong> : 3 ans à compter de la création du lead, à des fins de preuve et de qualité du service</li>
              <li><strong>Comptes utilisateurs agences</strong> : pendant toute la durée de la relation contractuelle, puis 3 ans après sa fin</li>
              <li><strong>Pièces comptables et factures</strong> : 10 ans (obligation légale)</li>
              <li><strong>Logs techniques et données de scan</strong> : 13 mois maximum</li>
              <li><strong>Données issues du formulaire de contact</strong> : 3 ans après le dernier échange</li>
            </ul>
          </section>

          <section id="securite" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Sécurité des données</h2>
            <p className="text-gray-700 mb-3">
              PLUGANDWORK met en œuvre les mesures techniques et organisationnelles appropriées
              pour garantir un niveau de sécurité adapté au risque :
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Chiffrement des communications en transit (HTTPS / TLS 1.3)</li>
              <li>Mots de passe stockés sous forme hachée (bcrypt)</li>
              <li>Sessions authentifiées et révocables</li>
              <li>Accès aux données restreint au strict besoin opérationnel</li>
              <li>Hébergement en France (OVH) sur infrastructure dédiée</li>
              <li>Sauvegardes régulières chiffrées</li>
            </ul>
          </section>

          <section id="droits" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Vos droits</h2>
            <p className="text-gray-700 mb-3">
              Conformément au RGPD, vous disposez à tout moment des droits suivants sur vos
              données personnelles :
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
              <li><strong>Droit d'accès</strong> : obtenir la confirmation que vos données sont traitées et en recevoir une copie</li>
              <li><strong>Droit de rectification</strong> : faire corriger des données inexactes ou incomplètes</li>
              <li><strong>Droit d'effacement</strong> (« droit à l'oubli ») : demander la suppression de vos données dans les cas prévus par le RGPD</li>
              <li><strong>Droit à la limitation</strong> du traitement</li>
              <li><strong>Droit à la portabilité</strong> : recevoir vos données dans un format structuré et lisible par machine</li>
              <li><strong>Droit d'opposition</strong>, notamment pour la prospection commerciale</li>
              <li><strong>Droit de retirer votre consentement</strong> à tout moment lorsque le traitement est fondé sur ce dernier</li>
              <li><strong>Droit de définir des directives</strong> relatives au sort de vos données après votre décès</li>
            </ul>
            <p className="text-gray-700">
              Pour exercer vos droits, contactez-nous à{' '}
              <a href="mailto:contact@sinoia.fr" className="text-primary-600 hover:underline">contact@sinoia.fr</a>
              {' '}en précisant votre demande et en joignant si nécessaire une copie d'une pièce
              d'identité. Nous répondons dans un délai maximum d'un mois.
            </p>
          </section>

          <section id="transferts" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Transferts hors Union européenne</h2>
            <p className="text-gray-700">
              L'agent conversationnel IA s'appuie sur les services d'OpenAI, dont les serveurs
              sont situés aux États-Unis. Ce transfert est encadré par les clauses contractuelles
              types adoptées par la Commission européenne. Aucun autre transfert de données hors
              UE n'est effectué. L'hébergement principal de vos données reste situé en France.
            </p>
          </section>

          <section id="contact" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact et réclamation</h2>
            <p className="text-gray-700 mb-3">
              Pour toute question relative à la présente politique ou au traitement de vos
              données, contactez-nous à :{' '}
              <a href="mailto:contact@sinoia.fr" className="text-primary-600 hover:underline">contact@sinoia.fr</a>.
            </p>
            <p className="text-gray-700">
              Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés,
              vous avez le droit d'introduire une réclamation auprès de la{' '}
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                Commission Nationale de l'Informatique et des Libertés (CNIL)
              </a>{' '}: 3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07.
            </p>
          </section>
        </article>
      </div>
    </MainLayout>
  )
}
